import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DayBookability, Suggestion, SuggestionGroup } from '@cfl/shared';

import { api } from './api';

/**
 * A week's worth of meetings, offered as suggestions.
 *
 * Named `calendarSuggestions` wherever this travels alongside grid state - the
 * grid already has a `suggestions` field meaning "pairings this person books
 * often" (see grid-api.ts). The two are unrelated concepts and must never be
 * conflated by sharing a name.
 */
export interface WeekSuggestions {
  connected: boolean;
  reachable: boolean;
  suggestions: Suggestion[];
  /** One row per meeting series - what the panel actually renders. */
  groups: SuggestionGroup[];
  /** Mon-first, seven entries, which days of this week may be booked at all. */
  days: DayBookability[];
  /**
   * The dismissed suggestions for this week, for the footer's headline count
   * AND its "Show" list - titles included, because the API already fetched
   * them from Google in this same request to compute `suggestions`/`groups`
   * (nothing new is persisted; see apps/api/src/calendar/suggestion.service.ts).
   * This is what makes restore reachable AFTER a reload, not just within the
   * panel session that dismissed something.
   *
   * The API also knows a raw dismissal-row count, which can be larger than
   * this array (a dismissal can outlive its event - deleted or moved off this
   * week in Google) - but it deliberately does not send it. A count promising
   * one more item than the "Show" list can actually list is worse than a
   * slightly smaller, fully explained one.
   */
  dismissedSuggestions: Suggestion[];
}

export const useCalendarSuggestions = (week: string) =>
  useQuery({
    queryKey: ['calendar', 'suggestions', week],
    queryFn: () => api.get<WeekSuggestions>(`/calendar/suggestions?week=${week}`),
  });

/**
 * Dismiss a group's occurrences (or a single expanded one) for this week.
 *
 * Persisted server-side - unlike the first version's in-state-only dismiss,
 * which vanished on reload with no way back. Invalidates the suggestions query
 * so the dismissed count and the remaining groups both refresh in one place.
 */
export const useDismissSuggestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { eventIds: string[]; week: string }) =>
      api.post<{ dismissed: number }>('/calendar/dismissals', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'suggestions'] });
    },
  });
};

/**
 * Bring a dismissed meeting back into the triage list, from the footer's
 * "Show" list. This is the undo the brief requires be reachable, not merely
 * possible - see CLAUDE.md, "an undo has to be reachable, not merely exist".
 */
export const useRestoreSuggestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { eventIds: string[] }) =>
      api.delete<{ restored: number }>('/calendar/dismissals', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'suggestions'] });
    },
  });
};

export interface AcceptItem {
  eventId: string;
  matchKey: string | null;
  workDate: string;
  projectId: string;
  activityId: string;
  durationSeconds: number;
  beginTime?: string;
  description?: string | null;
}

/**
 * Mirrors `acceptBody`'s `.max(50)` in apps/api/src/timesheet/timesheet.controller.ts -
 * a Zod failure past that cap comes back as the generic "Some fields were not
 * accepted.", which names no number and nothing a person can act on. The panel
 * checks this BEFORE posting, so it can disable Accept All and say why instead
 * of sending a batch the API is only going to refuse.
 *
 * If the API's cap ever changes, this constant has to change with it - that is
 * the whole reason it is named and commented rather than a bare 50 in the
 * component.
 */
export const MAX_ACCEPT_ITEMS = 50;

/**
 * On success, both the grid and the suggestions for this week go stale: the
 * accepted meeting must disappear from the ghosts and appear as a real entry in
 * the same refresh, or the two views disagree about what has been booked.
 *
 * Invalidated on FAILURE too (`onSettled`, not `onSuccess`): acceptCalendar is
 * deliberately not transactional (see its doc comment in timesheet.service.ts)
 * - "Added 2 of 5, the next one was refused" still wrote 2 real entries before
 * throwing. Refetching only on success would leave the panel showing a group's
 * pre-accept occurrence count after a partial failure, which is exactly the
 * stale state the panel's own accept handlers have to prune ticks against.
 */
export const useAcceptSuggestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: AcceptItem[]) =>
      api.post<{ created: number }>('/timesheet/entries/from-calendar', { items }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'suggestions'] });
    },
  });
};

export interface CalendarConnection {
  connected: boolean;
  connectedAt: string | null;
  lastSyncedAt: string | null;
}

export const useCalendarConnection = () =>
  useQuery({
    queryKey: ['calendar', 'connection'],
    queryFn: () => api.get<CalendarConnection>('/calendar/connection'),
  });

export async function fetchConnectUrl() {
  return api.get<{ url: string }>('/calendar/connect-url');
}

export const useSubmitCalendarCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      api.post<{ connected: true }>('/calendar/callback', { code, state }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'connection'] });
    },
  });
};

export const useDisconnectCalendar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ connected: false }>('/calendar/connection'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'connection'] });
      // A disconnected calendar offers no more meetings - drop whatever the grid
      // was showing rather than let a stale suggestion sit there unreachable.
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'suggestions'] });
    },
  });
};
